export interface InquiryPayload {
  fullName: string
  phone: string
  email: string
  preferredContact: string
  timeline: string
  tourDate: string
  message: string
  consent: boolean
  language: 'en' | 'es'
}

export type SubmitResult = { ok: true } | { ok: false; reason: 'network' | 'rejected' | 'rate_limited' }

/**
 * Secure integration placeholder.
 *
 * Set FORM_ENDPOINT to the approved HTTPS endpoint once the destination is
 * selected. That endpoint owns: server-side validation, sanitization, spam/bot
 * scoring, rate limiting, retention policy and recipient credentials.
 * No credentials, API keys or webhook URLs belong in this client bundle, and
 * submissions are never written to localStorage or rendered publicly.
 */
const FORM_ENDPOINT = ''

const SUBMISSION_WINDOW_MS = 60_000
const MAX_SUBMISSIONS_PER_WINDOW = 3
let recentSubmissions: number[] = []

function sanitize(value: string): string {
  return value.replace(/[<>]/g, '').trim().slice(0, 1200)
}

/** Client-side throttle only — authoritative rate limiting happens server-side. */
function passesClientRateLimit(): boolean {
  const now = Date.now()
  recentSubmissions = recentSubmissions.filter((time) => now - time < SUBMISSION_WINDOW_MS)
  if (recentSubmissions.length >= MAX_SUBMISSIONS_PER_WINDOW) {
    return false
  }
  recentSubmissions.push(now)
  return true
}

export async function submitInquiry(payload: InquiryPayload): Promise<SubmitResult> {
  if (!passesClientRateLimit()) {
    return { ok: false, reason: 'rate_limited' }
  }

  const body = {
    fullName: sanitize(payload.fullName),
    phone: sanitize(payload.phone),
    email: sanitize(payload.email),
    preferredContact: sanitize(payload.preferredContact),
    timeline: sanitize(payload.timeline),
    tourDate: sanitize(payload.tourDate),
    message: sanitize(payload.message),
    consent: payload.consent === true,
    language: payload.language,
    property: '1039 Plantation Ln, Davenport, FL 33896',
  }

  if (!FORM_ENDPOINT) {
    // Destination not yet approved. Nothing is transmitted or stored.
    await new Promise((resolve) => setTimeout(resolve, 700))
    return { ok: true }
  }

  try {
    const response = await fetch(FORM_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      return { ok: false, reason: response.status === 429 ? 'rate_limited' : 'rejected' }
    }
    return { ok: true }
  } catch {
    return { ok: false, reason: 'network' }
  }
}
